/**
 * The property an Evo caller is allowed to configure.
 *
 * Neither the native tab URL nor the Access Identity token names a STAAH
 * property — Evo only identifies the organisation — so the API resolves it
 * server-side (organisation claim → platform org row → active subscription →
 * property link) and `GET /evo/context` returns it in the encrypted form the
 * admin panel screens already use.
 *
 * An organisation can hold several chatbot subscriptions — one Evo menu entry
 * each — so the instance the user opened is sent along; without it the API
 * falls back to the organisation's first subscription.
 */
import { notifyUnauthorised, type RequestHeaders } from './runtime';

export type EvoContext = {
  propertyId: string | null;
  groupId: string | null;
  /** Subscription the API resolved to; echoed back on every later request. */
  subscriptionId: string | null;
  /** Name of the Evo subscription, used as the default property name. */
  instanceName: string | null;
  isNew: boolean;
};

export async function fetchEvoContext(
  baseUrl: string,
  getHeaders: () => Promise<RequestHeaders>,
  instanceId?: string | null,
): Promise<EvoContext | null> {
  const query = instanceId ? `?instanceId=${encodeURIComponent(instanceId)}` : '';
  const response = await fetch(`${baseUrl}/evo/context${query}`, {
    headers: { 'Content-Type': 'application/json', ...(await getHeaders()) },
  });

  if (response.status === 401) {
    notifyUnauthorised();
    return null;
  }

  // 400 means the API did not receive an Evo token, which is the normal case
  // outside Evo: the legacy token already carries the property.
  if (response.status === 400) {
    return null;
  }

  const body = (await response.json().catch(() => ({}))) as {
    error?: string;
    propertyID?: string | null;
    groupID?: string | null;
    subscriptionID?: string | null;
    instanceName?: string | null;
    isNew?: boolean;
  };

  if (!response.ok) {
    throw new Error(
      body.error ??
        'ChatOBI is not enabled for this organisation. Please contact Access support.',
    );
  }

  return {
    propertyId: body.propertyID ?? null,
    groupId: body.groupID ?? null,
    subscriptionId: body.subscriptionID ?? null,
    instanceName: body.instanceName ?? null,
    isNew: Boolean(body.isNew),
  };
}
